/**
 * Shared CSS-grid geometry for the Master Schedule v3 prototype grids.
 *
 * Both the Coverage axis (rows = staffing slots) and the People axis
 * (rows = nurses) lay their day columns out on the SAME calendar lattice so
 * the two views line up day-for-day when you flip the axis toggle. This module
 * is the single source of truth for that lattice: leading columns, per-week
 * 7-day blocks, week dividers, and the row height. Changing a width here moves
 * both grids in lockstep — never hardcode these in a grid component.
 */

import { createElement, Fragment, type ReactNode } from "react";

/**
 * Leading columns. The Coverage grids split identity into three tracks —
 * Location · Shift · Role — so each reads as its own column (no "Shift · Role"
 * cramming). The People grid has its own, shorter lattice: a single staff
 * identity track (see `LEADING_PEOPLE_STAFF`). The two axes therefore differ in
 * leading width; what they share is the per-week 7-day + divider structure, which
 * is what makes the day columns read the same.
 *
 * `*_COMPACT` widths are the Detail-mode override: Detail cares about the NAME
 * over the slot identity, so the leading zone shrinks to hand the freed width to
 * the name chips. Both Coverage grids pass the variant they want into
 * `gridColumns`/`leadingOffsets`; People always uses the standard widths.
 */
// Coverage Summary widths: Location + Role (Job) get generous room to read in full; the
// extra leading width is reclaimed from the 1fr day columns, tightening the per-day grid
// without touching the shared DAY_COL (which would also shrink People/Detail days).
export const LEADING_LOCATION = "176px";
export const LEADING_SHIFT = "96px";
export const LEADING_ROLE = "132px";

/** Detail-mode compact leading widths — reclaim space for the name chips. */
export const LEADING_LOCATION_COMPACT = "104px";
export const LEADING_SHIFT_COMPACT = "72px";
export const LEADING_ROLE_COMPACT = "92px";

/**
 * People-axis leading widths: ROLE · STAFF · LOCATION. Role and location are their own tracks (not
 * tokens inside the name cell) because each is a sort key, and a sortable column needs a header of
 * its own to click. The role track fits the longest codes (RLPN, LCSW) plus the header's sort caret;
 * the staff track keeps ordinary names readable without ellipsizing; the location track fits a unit
 * name plus the "+N" count of the other units worked in the window; a long unit name ellipsizes and
 * reads in full in the cell's tooltip, so the track is sized for common unit names, not the longest.
 */
export const LEADING_PEOPLE_ROLE = "72px";
export const LEADING_PEOPLE_STAFF = "200px";
export const LEADING_PEOPLE_LOCATION = "120px";

/** Sticky `left` offsets of the People leading tracks, derived from their widths so they stack flush. */
export const PEOPLE_LEADING_OFFSETS = {
	role: 0,
	staff: LEADING_PEOPLE_ROLE,
	location: `calc(${LEADING_PEOPLE_ROLE} + ${LEADING_PEOPLE_STAFF})`,
} as const;

/**
 * Grid columns for the People axis: ROLE | STAFF | LOCATION | (7 days + divider) × weeks.
 *
 * `compact` is the normalized (multi-week) view: day cells shrink to a dot, so the columns shrink to
 * a fixed narrow track instead of the flexible `DAY_COL`. Six weeks of narrow columns usually fits
 * without scrolling at all, and overflows gracefully when it doesn't — whereas `1fr` would have kept
 * the full width and just squeezed each day thinner.
 */
export const gridColumnsPeople = (weeks: number, compact = false): string => {
	const week = `repeat(${DAYS_PER_WEEK}, ${compact ? DAY_COL_COMPACT : DAY_COL})`;
	const days = Array.from({ length: weeks }, (_, i) => (i < weeks - 1 ? `${week} ${DIVIDER_W}` : week)).join(" ");
	return `${LEADING_PEOPLE_ROLE} ${LEADING_PEOPLE_STAFF} ${LEADING_PEOPLE_LOCATION} ${days}`;
};

/** Leading track count per axis — Coverage keeps Location · Shift · Role; People has Role · Staff · Location. */
export const LEADING_TRACKS_COVERAGE = 3;
export const LEADING_TRACKS_PEOPLE = 3;

/** Days in one calendar week — the lattice's fixed period, Sunday-first. */
export const DAYS_PER_WEEK = 7;

/** Row height for a single body row — sized for a clean dense vertical rhythm. */
export const ROW_H = "32px";
/** Min height of an expanded / detail row where name chips wrap. Shared by the
 * Summary grid's in-place detail and the Detail grid so both rows line up. */
export const EXPANDED_ROW_MIN_H = "44px";
/** Narrowest a flexible day column may get. */
export const DAY_COL_MIN = "36px";
/** A single day column — flexes between a floor and an equal share. */
export const DAY_COL = `minmax(${DAY_COL_MIN}, 1fr)`;
export const DAY_COL_COMPACT = "40px";
/** Diameter of the normalized view's per-day dot. */
export const DAY_DOT_SIZE = 12;
/** Width of the visual divider track between two weeks. */
export const DIVIDER_W = "10px";

/** A leading-column width preset — standard or the Detail-mode compact set. */
export type LeadingVariant = "standard" | "compact";

/** The three leading-column widths for a given variant, in [Location, Shift, Role] order. */
export const leadingWidths = (variant: LeadingVariant = "standard"): [string, string, string] =>
	variant === "compact" ? [LEADING_LOCATION_COMPACT, LEADING_SHIFT_COMPACT, LEADING_ROLE_COMPACT] : [LEADING_LOCATION, LEADING_SHIFT, LEADING_ROLE];

/**
 * Sticky `left` offsets for the 2nd and 3rd leading columns (the 1st is always
 * `0`). Derived from the leading widths so the three sticky columns stack
 * flush — never hardcode these in a grid component.
 */
export const leadingOffsets = (variant: LeadingVariant = "standard"): { shift: string; role: string } => {
	const [loc, shift] = leadingWidths(variant);
	return { shift: loc, role: `calc(${loc} + ${shift})` };
};

/** Grid columns: Location | Shift | Role | (7 days + divider) × weeks. */
export const gridColumns = (weeks: number, variant: LeadingVariant = "standard"): string => {
	const [loc, shift, role] = leadingWidths(variant);
	const week = `repeat(7, ${DAY_COL})`;
	const days = Array.from({ length: weeks }, (_, i) => (i < weeks - 1 ? `${week} ${DIVIDER_W}` : week)).join(" ");
	return `${loc} ${shift} ${role} ${days}`;
};

/** Total Coverage grid tracks incl. dividers, so a full-width row can span everything. */
export const totalTracks = (weeks: number): number => LEADING_TRACKS_COVERAGE + 7 * weeks + (weeks - 1);

/** Column index (1-based) where the People day grid starts — after its leading tracks. */
export const FIRST_DAY_COL_PEOPLE = LEADING_TRACKS_PEOPLE + 1;

/**
 * Walk the calendar lattice (weeks × 7 Sunday-first days) once, emitting a flat
 * list of grid children — the per-day node from `renderCell`, plus (optionally)
 * a week-divider track after each week but the last. Replaces the `Array.from`
 * double-loop + `d === 6 && w < weeks - 1` divider guard copied across every
 * header/body block of the three grids, so the lattice can't drift between them.
 */
export const mapCalendarCells = (
	weeks: number,
	renderCell: (weekIdx: number, dayIdx: number, globalIdx: number) => ReactNode,
	renderDivider?: (weekIdx: number) => ReactNode,
): ReactNode[] => {
	const nodes: ReactNode[] = [];
	for (let w = 0; w < weeks; w++) {
		for (let d = 0; d < 7; d++) {
			nodes.push(createElement(Fragment, { key: `cell-${w}-${d}` }, renderCell(w, d, w * 7 + d)));
		}
		if (renderDivider && w < weeks - 1) {
			nodes.push(createElement(Fragment, { key: `div-${w}` }, renderDivider(w)));
		}
	}
	return nodes;
};

/**
 * Opacity of the hovered row's top/bottom hairline. Strong enough to trace the row across a wide grid,
 * light enough that it reads as a guide rather than a selection — the cells' own job/role hues stay the
 * loudest thing in the row.
 */
export const ROW_BAND_ALPHA = 0.45;
